/**
 * Rotary knobs and vertical faders.
 *
 * Built rather than styled from `<input type="range">` because a rotary control
 * needs drag-distance semantics that a native range does not have: vertical
 * travel regardless of where the pointer starts, fine adjustment on shift, and
 * a value that does not jump to wherever you happened to click.
 */

/** Pixels of vertical drag to travel the whole range. */
const DRAG_TRAVEL = 180;
const FINE_FACTOR = 0.25;

/** Rotary sweep, in degrees either side of vertical. */
const SWEEP = 135;

export interface ControlOptions {
  label: string;
  value: number;
  /** Value restored by double-click. */
  defaultValue?: number;
  /** Drawn filling outward from the centre rather than up from the bottom. */
  bipolar?: boolean;
  format?: (value: number) => string;
  onChange: (value: number) => void;
  /** Notified on interaction start, so auto-mix can yield. */
  onGrab?: () => void;
}

abstract class Control {
  readonly element: HTMLElement;
  protected value: number;
  protected readonly options: Required<Pick<ControlOptions, "defaultValue" | "bipolar">> &
    ControlOptions;

  constructor(options: ControlOptions) {
    this.options = {
      defaultValue: options.defaultValue ?? options.value,
      bipolar: options.bipolar ?? false,
      ...options,
    };
    this.value = clamp(options.value);
    this.element = document.createElement("div");
    this.element.tabIndex = 0;
    this.element.setAttribute("role", "slider");
    this.element.setAttribute("aria-label", options.label);
  }

  protected bindPointer(): void {
    let dragging: { pointerId: number; startY: number; startValue: number } | null = null;

    this.element.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      this.options.onGrab?.();
      this.element.setPointerCapture(event.pointerId);
      this.element.classList.add("grabbing");
      dragging = { pointerId: event.pointerId, startY: event.clientY, startValue: this.value };
    });

    this.element.addEventListener("pointermove", (event) => {
      if (!dragging || event.pointerId !== dragging.pointerId) return;
      // Relative to where the drag began, not absolute position: grabbing a
      // knob should never make its value jump.
      const travel = (dragging.startY - event.clientY) / DRAG_TRAVEL;
      const scale = event.shiftKey ? FINE_FACTOR : 1;
      this.set(dragging.startValue + travel * scale);
    });

    const release = () => {
      dragging = null;
      this.element.classList.remove("grabbing");
    };
    this.element.addEventListener("pointerup", release);
    this.element.addEventListener("pointercancel", release);
    this.element.addEventListener("lostpointercapture", release);

    this.element.addEventListener("dblclick", (event) => {
      event.preventDefault();
      this.set(this.options.defaultValue);
    });

    this.element.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        this.options.onGrab?.();
        const step = (event.shiftKey ? 0.005 : 0.02) * (event.deltaY > 0 ? -1 : 1);
        this.set(this.value + step);
      },
      { passive: false },
    );

    this.element.addEventListener("keydown", (event) => {
      const step = event.shiftKey ? 0.005 : 0.02;
      switch (event.key) {
        case "ArrowUp":
        case "ArrowRight":
          event.preventDefault();
          this.options.onGrab?.();
          this.set(this.value + step);
          break;
        case "ArrowDown":
        case "ArrowLeft":
          event.preventDefault();
          this.options.onGrab?.();
          this.set(this.value - step);
          break;
        case "Home":
          event.preventDefault();
          this.set(this.options.defaultValue);
          break;
        default:
      }
    });
  }

  set(value: number, notify = true): void {
    const next = clamp(value);
    if (next === this.value) return;
    this.value = next;
    this.render();
    if (notify) this.options.onChange(next);
  }

  get(): number {
    return this.value;
  }

  reset(notify = true): void {
    this.set(this.options.defaultValue, notify);
  }

  protected formatted(): string {
    return this.options.format ? this.options.format(this.value) : this.value.toFixed(2);
  }

  protected updateAria(): void {
    this.element.setAttribute("aria-valuenow", this.value.toFixed(3));
    this.element.setAttribute("aria-valuetext", this.formatted());
  }

  protected abstract render(): void;
}

function clamp(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function polar(cx: number, cy: number, r: number, degrees: number): [number, number] {
  const radians = ((degrees - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(radians), cy + r * Math.sin(radians)];
}

function arcPath(cx: number, cy: number, r: number, from: number, to: number): string {
  if (Math.abs(to - from) < 0.01) return "";
  const [x0, y0] = polar(cx, cy, r, from);
  const [x1, y1] = polar(cx, cy, r, to);
  const large = Math.abs(to - from) > 180 ? 1 : 0;
  const sweep = to > from ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

export class Knob extends Control {
  private readonly arc: SVGPathElement;
  private readonly pointer: SVGLineElement;
  private readonly readout: HTMLElement;

  constructor(options: ControlOptions) {
    super(options);
    this.element.className = "knob-ctl";
    this.element.innerHTML = `
      <svg viewBox="0 0 44 44" aria-hidden="true">
        <path class="knob-bg" d="${arcPath(22, 22, 16, -SWEEP, SWEEP)}" />
        <path class="knob-arc" d="" />
        <circle class="knob-body" cx="22" cy="22" r="11" />
        <line class="knob-pointer" x1="22" y1="22" x2="22" y2="11" />
      </svg>
      <span class="ctl-name">${options.label}</span>
      <span class="ctl-value num"></span>
    `;
    this.arc = this.element.querySelector(".knob-arc") as SVGPathElement;
    this.pointer = this.element.querySelector(".knob-pointer") as SVGLineElement;
    this.readout = this.element.querySelector(".ctl-value") as HTMLElement;
    this.bindPointer();
    this.render();
  }

  protected render(): void {
    const angle = -SWEEP + this.value * SWEEP * 2;
    // Bipolar controls fill outward from 12 o'clock, so "flat" reads at a
    // glance as an empty arc rather than a half-full one.
    const from = this.options.bipolar ? 0 : -SWEEP;
    this.arc.setAttribute("d", arcPath(22, 22, 16, from, angle));
    this.pointer.setAttribute("transform", `rotate(${angle} 22 22)`);
    this.readout.textContent = this.formatted();
    this.updateAria();
  }
}

export class Fader extends Control {
  private readonly cap: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly readout: HTMLElement;

  constructor(options: ControlOptions) {
    super(options);
    this.element.className = "fader-ctl";
    this.element.innerHTML = `
      <div class="fader-track">
        <div class="fader-fill"></div>
        <div class="fader-cap"></div>
      </div>
      <span class="ctl-name">${options.label}</span>
      <span class="ctl-value num"></span>
    `;
    this.cap = this.element.querySelector(".fader-cap") as HTMLElement;
    this.fill = this.element.querySelector(".fader-fill") as HTMLElement;
    this.readout = this.element.querySelector(".ctl-value") as HTMLElement;
    this.bindPointer();
    this.render();
  }

  protected render(): void {
    const percent = this.value * 100;
    this.cap.style.bottom = `calc(${percent}% - 7px)`;
    this.fill.style.height = `${percent}%`;
    this.readout.textContent = this.formatted();
    this.updateAria();
  }
}
